import { useMutation } from '@tanstack/react-query';

import { useToast } from '@repo/ui/Toast/ToastRoot';
import {
  submitMutationFn,
  type SubmitVariables,
} from '../graphql/mutation/submit';
import {
  type DeliveryOption,
  getDeliveryQueryFn,
} from '../graphql/query/getDelivery';
import { queryClient } from '../queryClient';
import { MUTATIONS, QUERIES } from '../utils/constants';
import { getInput } from '../components/tasks/helpers';
import { extractReasons } from '../utils/item';
import { clientLogger } from '../utils/logger';
import { generateQueryKey } from '../utils/queries';
import { type WorkflowData } from '../workflow';
import { useWorkflow } from '../workflow/hooks';
import { useResolutions } from './useResolutions';
import { rawString } from '../components/Language/Language';
import { getParams } from '../utils/searchParams';
import { pdxEvent } from '../utils/noflake';
import { addShipmentScopeItemsToSubmitData } from '../utils/shipmentScopeUtils';
import { useErrorHandler } from '../utils/handleErrorCodes';

export const useSubmit = ({
  formData,
  onSuccess,
}: {
  formData?: WorkflowData | null;
  onSuccess?: () => void;
} = {}) => {
  const {
    config,
    data: workflowData,
    order,
    requestId,
    updateIsSaving,
  } = useWorkflow();

  const { errorToast } = useToast();
  const handleErrorCodes = useErrorHandler();

  const { mutate, isPending, data, error } = useMutation({
    mutationFn: async ({ input }: { input: SubmitVariables }) => {
      pdxEvent({
        event_category: 'set_data',
        event_type: 'submit',
        event_value: 'submit',
      });
      updateIsSaving(true);
      return submitMutationFn({ input });
    },
    onSuccess: (data) => {
      pdxEvent({
        event_category: 'set_data',
        event_type: 'submit_success',
        event_value: 'submit',
      });
      updateIsSaving(false);
      queryClient.setQueryData([MUTATIONS.SUBMIT], data);
      onSuccess?.();
    },
    onError: (error: unknown) => {
      handleErrorCodes(error);
      pdxEvent({
        event_category: 'error',
        event_type: 'submit_error',
        event_value: 'set_data failed to set submit',
      });
      updateIsSaving(false);
      errorToast({
        message:
          rawString({
            langKey: 'Global.submit.error',
            fallback:
              'There was a problem with your request. Please try again.',
          }) ?? '',
      });
      clientLogger.logError(error);
    },
  });

  const resolutionsByItem = useResolutions();

  const submit = async () => {
    if (!formData || !formData.items) return; // TODO: Need better error handling for this;

    // Check for shipment scope reasons and add items if needed
    const updatedFormData = addShipmentScopeItemsToSubmitData(formData, order);

    const params = getParams();
    const { items, returnMethod, refund, locations } = updatedFormData!;

    const hasReturnMethod = returnMethod && 'type' in returnMethod;

    const { customer, orderId, items: allItems, orderTokens } = order || {};

    const filteredOrderItems =
      allItems?.filter(({ id }) => {
        // Check if this order item ID matches any form item, either directly
        // or as part of a composite key like "itemId-index" or "itemId-trackingNumber"
        return Object.keys(items || {}).some((formKey) => {
          // First check direct match
          if (formKey === id) return true;

          // Check if this form key contains this item ID
          const formItemData = items?.[formKey];
          const actualItemId = formItemData?.itemSelection?.id;
          return actualItemId === id;
        });
      }) ?? [];

    const formPostalCode =
      locations && 'postalCode' in locations
        ? locations?.postalCode
        : undefined;
    const orderPostalCode = order?.shippingAddress?.postalCode;
    const postalCode = formPostalCode ?? orderPostalCode;

    if (!orderId) {
      throw new Error('Missing orderId or firstItemId');
    }

    const reasonsByItem = extractReasons({
      data: updatedFormData,
      order,
    });

    const queryKey = generateQueryKey(QUERIES.GET_DELIVERY, {
      taskIds: ['reason', 'resolution'],
      workflowConfig: config,
      workflowData,
    });

    const deliveryData = await queryClient.ensureQueryData({
      queryKey: [...queryKey, postalCode],
      queryFn: async () => {
        try {
          return await getDeliveryQueryFn({
            ...getInput({
              customer,
              items: filteredOrderItems,
              formItems: items,
              orderTokens,
              orderId,
              reasons: reasonsByItem,
              refund: refund && 'method' in refund ? refund : undefined,
              requestId,
              resolutionsByItem,
            }),
            ...(postalCode && {
              locations: {
                locale: params?.locale ?? 'en_US',
                postalCode: postalCode ?? '',
              },
            }),
          });
        } catch (error: unknown) {
          handleErrorCodes(error);
          throw error;
        }
      },
    });

    const deliveryMethod: DeliveryOption | undefined =
      deliveryData?.deliveryOptions.find(({ id }) => {
        return hasReturnMethod ? id === returnMethod?.type : false;
      });

    const location = deliveryMethod?.locations?.find(({ id }) => {
      return hasReturnMethod ? id === returnMethod?.location : false;
    });

    mutate({
      input: getInput({
        customer,
        deliveryMethod: deliveryMethod
          ? {
              ...deliveryMethod,
              address: returnMethod?.address,
            }
          : undefined,
        items: filteredOrderItems,
        formItems: items,
        location,
        orderTokens,
        orderId,
        reasons: reasonsByItem,
        refund: refund && 'method' in refund ? refund : undefined,
        requestId,
        resolutionsByItem,
      }),
    });
  };

  return {
    submit,
    isLoading: isPending,
    response: data,
    error,
  };
};
